/**
 * StockTab — current stock levels + ledger drawer.
 *
 * Sprint 2026-08 · Phase 7.
 *
 * Owner spec:
 *   • Every scanned vendor bill writes stock IN entries.
 *   • Every Petpooja sale (via recipe) writes stock OUT entries.
 *   • Negative stock is allowed silently (QSR reality — sale sometimes
 *     happens before the delivery bill is scanned).
 *   • Click any raw material to see the full append-only ledger.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    Boxes,
    ArrowUpRight,
    ArrowDownRight,
    Package,
    Cog,
    RefreshCcw,
    ClipboardList,
    History,
    ClipboardCheck,
    AlertTriangle,
    Loader2,
    BarChart3,
    Ban,
    Sparkles,
    GitMerge,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { api, fmtDate, fmtMoney } from '../../lib/api';


export function StockTab() {
    const [levels, setLevels] = useState(null);
    const [uomList, setUomList] = useState([]);
    const [lowStock, setLowStock] = useState([]);
    const [openLedger, setOpenLedger] = useState(null);      // item row for ledger drawer
    const [openUomEdit, setOpenUomEdit] = useState(null);    // item row for UOM edit
    const [openStockTake, setOpenStockTake] = useState(false);
    const [openAuto86, setOpenAuto86] = useState(null);      // item row triggering 86
    const [openVariance, setOpenVariance] = useState(false); // variance report sheet
    // Sprint 2026-08 · Layers 2 & 3 — dedup workflow
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [openMerge, setOpenMerge] = useState(false);
    const [openCluster, setOpenCluster] = useState(false);
    const [confirmBackfill, setConfirmBackfill] = useState(false);
    const [backfilling, setBackfilling] = useState(false);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLevels(null);
        try {
            const [lv, uom, low] = await Promise.all([
                api.stockLevels(),
                api.stockUomMaster(),
                api.stockLow().catch(() => ({ items: [] })),
            ]);
            setLevels(lv.items || []);
            setUomList(uom.units || []);
            setLowStock(low.items || []);
        } catch (e) {
            setLevels([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Set of item_ids currently below reorder level for quick badge lookup.
    const lowStockIds = useMemo(
        () => new Set((lowStock || []).map((r) => r.item_id)),
        [lowStock],
    );

    async function runBackfill() {
        setConfirmBackfill(false);
        setBackfilling(true);
        try {
            const r = await api.stockBackfillFromBills();
            toast.success(
                `Backfilled ${r.ledger_entries_written} stock entries from ` +
                `${r.bills_with_items}/${r.bills_scanned} bills.` +
                (r.bills_failed ? ` ${r.bills_failed} failed.` : ''),
                { duration: 6000 },
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Backfill failed.');
        } finally {
            setBackfilling(false);
        }
    }

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (levels || []).filter((r) =>
            !q || (r.item_name || '').toLowerCase().includes(q),
        );
    }, [levels, search]);

    const totals = useMemo(() => {
        return (levels || []).reduce(
            (acc, r) => {
                acc.rows++;
                if (r.qty > 0) acc.positive++;
                else if (r.qty < 0) acc.negative++;
                else acc.zero++;
                return acc;
            },
            { rows: 0, positive: 0, negative: 0, zero: 0 },
        );
    }, [levels]);

    return (
        <div className="space-y-4" data-testid="stock-tab">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h3 className="font-heading text-lg flex items-center gap-2">
                        <Boxes size={18} className="text-primary" /> Stock levels
                    </h3>
                    <p className="text-xs text-muted-foreground max-w-lg">
                        Live stock per raw material, computed from every vendor bill
                        (IN) and Petpooja sale (OUT). Negative stock is allowed — it
                        auto-corrects when the vendor bill lands.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[11px] flex items-center gap-3 text-muted-foreground">
                        <span>{totals.rows} items</span>
                        <span className="text-green-700 dark:text-green-400">
                            {totals.positive} in stock
                        </span>
                        {totals.negative > 0 && (
                            <span className="text-red-700 dark:text-red-400">
                                {totals.negative} negative
                            </span>
                        )}
                        {lowStock.length > 0 && (
                            <span
                                className="text-amber-700 dark:text-amber-400 font-medium"
                                data-testid="stock-lowstock-count"
                            >
                                {lowStock.length} low
                            </span>
                        )}
                    </div>
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search…"
                        className="w-[180px]"
                        data-testid="stock-search"
                    />
                    <Button
                        size="sm"
                        variant="ghost"
                        className="gap-1"
                        onClick={load}
                        title="Refresh"
                    >
                        <RefreshCcw size={14} /> Refresh
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => setOpenCluster(true)}
                        title="AI-detect duplicate raw materials (same product, different vendor names)"
                        data-testid="stock-cluster-btn"
                    >
                        <Sparkles size={13} /> Auto-detect dupes
                    </Button>
                    {selectedIds.size >= 2 && (
                        <Button
                            size="sm"
                            className="gap-1"
                            onClick={() => setOpenMerge(true)}
                            data-testid="stock-merge-btn"
                        >
                            <GitMerge size={13} /> Merge {selectedIds.size}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => setOpenVariance(true)}
                        title="Weekly variance report (theoretical vs physical usage)"
                        data-testid="stock-variance-btn"
                    >
                        <BarChart3 size={13} /> Variance
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => setConfirmBackfill(true)}
                        disabled={backfilling}
                        title="One-time: generate stock IN entries for all past vendor bills"
                        data-testid="stock-backfill-btn"
                    >
                        {backfilling
                            ? <><Loader2 size={13} className="animate-spin" /> Backfilling…</>
                            : <><History size={13} /> Backfill bills</>}
                    </Button>
                    <Button
                        size="sm"
                        className="gap-1"
                        onClick={() => setOpenStockTake(true)}
                        data-testid="stock-do-take-btn"
                    >
                        <ClipboardCheck size={13} /> Do stock take
                    </Button>
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {levels === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-9" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Package size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">
                            {(levels || []).length === 0 ? 'No stock activity yet' : 'Nothing matches'}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {(levels || []).length === 0
                                ? 'Scan a vendor bill or upload a Petpooja Excel file to see stock movements here. Have older bills? Use "Backfill bills".'
                                : 'Try a different search.'}
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="stock-table">
                            <thead className="bg-muted/40 text-muted-foreground">
                                <tr>
                                    <th className="w-8 px-3"></th>
                                    <th className="text-left px-4 py-2 font-medium">Raw material</th>
                                    <th className="text-right px-4 py-2 font-medium">In (all-time)</th>
                                    <th className="text-right px-4 py-2 font-medium">Out (all-time)</th>
                                    <th className="text-right px-4 py-2 font-medium">Current stock</th>
                                    <th className="text-left px-4 py-2 font-medium">UOM</th>
                                    <th className="text-left px-4 py-2 font-medium">Last activity</th>
                                    <th className="text-right px-4 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const negative = r.qty < 0;
                                    const zero = r.qty === 0;
                                    const isLow = lowStockIds.has(r.item_id);
                                    const tone = negative
                                        ? 'text-red-700 dark:text-red-400 font-semibold'
                                        : zero
                                            ? 'text-muted-foreground'
                                            : isLow
                                                ? 'text-amber-700 dark:text-amber-400 font-semibold'
                                                : 'text-green-700 dark:text-green-400 font-semibold';
                                    return (
                                        <tr
                                            key={r.item_id}
                                            className="border-t border-border hover:bg-muted/30 cursor-pointer"
                                            onClick={() => setOpenLedger(r)}
                                            data-testid={`stock-row-${r.item_id}`}
                                        >
                                            <td className="px-3" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedIds.has(r.item_id)}
                                                    onChange={() => {
                                                        setSelectedIds((prev) => {
                                                            const nxt = new Set(prev);
                                                            if (nxt.has(r.item_id)) nxt.delete(r.item_id);
                                                            else nxt.add(r.item_id);
                                                            return nxt;
                                                        });
                                                    }}
                                                    data-testid={`stock-select-${r.item_id}`}
                                                />
                                            </td>
                                            <td className="px-4 py-2 font-medium">
                                                {r.item_name}
                                                {r.mixed_uoms && (
                                                    <Badge variant="outline" className="ml-2 text-[10px] border-amber-400/60 text-amber-700 dark:text-amber-300">
                                                        mixed UOM
                                                    </Badge>
                                                )}
                                                {isLow && (
                                                    <Badge
                                                        variant="outline"
                                                        className="ml-2 text-[10px] gap-1 border-amber-400/60 text-amber-700 dark:text-amber-300"
                                                        data-testid={`stock-low-badge-${r.item_id}`}
                                                    >
                                                        <AlertTriangle size={10} /> low
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                <ArrowUpRight size={11} className="inline mr-0.5 text-green-600" />
                                                {r.in_qty}
                                            </td>
                                            <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                <ArrowDownRight size={11} className="inline mr-0.5 text-red-500" />
                                                {r.out_qty}
                                            </td>
                                            <td className={`px-4 py-2 text-right tabular-nums font-mono ${tone}`}>
                                                {r.qty}
                                            </td>
                                            <td className="px-4 py-2 text-xs uppercase text-muted-foreground">
                                                {r.stock_uom || '—'}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-muted-foreground">
                                                {fmtDate(r.last_activity_at)}
                                            </td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="flex items-center justify-end gap-1">
                                                    {r.qty <= 0 && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="h-7 gap-1 text-xs border-red-400/60 text-red-700 dark:text-red-400"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setOpenAuto86(r);
                                                            }}
                                                            title="86 all menu items using this ingredient"
                                                            data-testid={`stock-86-${r.item_id}`}
                                                        >
                                                            <Ban size={12} /> 86
                                                        </Button>
                                                    )}
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        className="h-7 gap-1 text-xs"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setOpenUomEdit(r);
                                                        }}
                                                        title="Set canonical unit and reorder level"
                                                        data-testid={`stock-uom-${r.item_id}`}
                                                    >
                                                        <Cog size={12} /> UOM
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {openLedger && (
                <LedgerDrawer
                    open={!!openLedger}
                    onOpenChange={(v) => { if (!v) setOpenLedger(null); }}
                    item={openLedger}
                />
            )}
            {openUomEdit && (
                <UomEditDialog
                    open={!!openUomEdit}
                    onOpenChange={(v) => { if (!v) setOpenUomEdit(null); }}
                    item={openUomEdit}
                    uomList={uomList}
                    onSaved={async () => { setOpenUomEdit(null); await load(); }}
                />
            )}
            {openStockTake && (
                <StockTakeSheet
                    open={openStockTake}
                    onOpenChange={setOpenStockTake}
                    onSaved={async () => { setOpenStockTake(false); await load(); }}
                />
            )}
            {openAuto86 && (
                <Auto86Dialog
                    open={!!openAuto86}
                    onOpenChange={(v) => { if (!v) setOpenAuto86(null); }}
                    item={openAuto86}
                    onSaved={async () => { setOpenAuto86(null); await load(); }}
                />
            )}
            {openVariance && (
                <VarianceReportSheet
                    open={openVariance}
                    onOpenChange={setOpenVariance}
                />
            )}
            {openMerge && (
                <MergeRawMaterialsDialog
                    open={openMerge}
                    onOpenChange={setOpenMerge}
                    selectedIds={Array.from(selectedIds)}
                    levels={levels || []}
                    onSaved={async () => { setOpenMerge(false); setSelectedIds(new Set()); await load(); }}
                />
            )}
            {openCluster && (
                <ClusterSuggestionsDialog
                    open={openCluster}
                    onOpenChange={setOpenCluster}
                    onSaved={async () => { setOpenCluster(false); await load(); }}
                />
            )}
            <AlertDialog open={confirmBackfill} onOpenChange={setConfirmBackfill}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Backfill stock from past bills?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This walks every vendor bill you've scanned and writes stock IN
                            ledger entries for each line item — including bills scanned
                            before the stock module existed. Safe to run multiple times
                            (idempotent). Large tenants may take a few seconds.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={runBackfill}>Backfill now</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}


function LedgerDrawer({ open, onOpenChange, item }) {
    const [entries, setEntries] = useState(null);
    useEffect(() => {
        if (!open) return;
        (async () => {
            setEntries(null);
            try {
                const r = await api.stockLedger({ item_id: item.item_id, limit: 500 });
                setEntries(r.items || []);
            } catch {
                setEntries([]);
            }
        })();
    }, [open, item.item_id]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <ClipboardList size={16} /> Ledger · {item.item_name}
                    </SheetTitle>
                    <SheetDescription>
                        Current stock: <span className="font-mono">{item.qty} {item.stock_uom}</span>
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-4">
                    {entries === null ? (
                        <div className="space-y-2">
                            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}
                        </div>
                    ) : entries.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">
                            No ledger entries.
                        </div>
                    ) : (
                        <div className="space-y-1.5">
                            {entries.map((e) => {
                                const isIn = e.direction === 'in';
                                return (
                                    <div
                                        key={e.id}
                                        className="flex items-start justify-between gap-3 px-2 py-1.5 rounded hover:bg-muted/40 text-xs"
                                    >
                                        <div className="flex items-start gap-2 min-w-0 flex-1">
                                            <span className={isIn ? 'text-green-600' : 'text-red-500'}>
                                                {isIn ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
                                            </span>
                                            <div className="min-w-0">
                                                <div className="font-medium">
                                                    {isIn ? '+' : '-'}{e.qty} <span className="text-muted-foreground">{e.uom}</span>
                                                </div>
                                                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                                                    {e.source_type}
                                                    {e.note && <span className="normal-case"> · {e.note}</span>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-muted-foreground whitespace-nowrap">
                                            {fmtDate(e.at)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}


function UomEditDialog({ open, onOpenChange, item, uomList, onSaved }) {
    const [uom, setUom] = useState(item.stock_uom || 'piece');
    const [displayName, setDisplayName] = useState(item.item_name || '');
    const [reorderLevel, setReorderLevel] = useState('');
    const [reorderUom, setReorderUom] = useState('');
    // Layer 1 (Sprint 2026-08): aliases so vendor-variant spellings all
    // resolve to this canonical raw material.
    const [aliases, setAliases] = useState([]);       // array of strings
    const [aliasDraft, setAliasDraft] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.stockRawMaterials();
                const meta = (r.items || []).find((x) => x.id === item.item_id);
                if (meta) {
                    if (meta.reorder_level != null) setReorderLevel(String(meta.reorder_level));
                    if (meta.reorder_uom) setReorderUom(meta.reorder_uom);
                    if (Array.isArray(meta.alt_names)) setAliases(meta.alt_names);
                }
            } catch { /* noop */ }
        })();
    }, [item.item_id]);

    const addAlias = () => {
        const t = aliasDraft.trim();
        if (!t) return;
        if (aliases.includes(t.toLowerCase())) { setAliasDraft(''); return; }
        setAliases((p) => [...p, t]);
        setAliasDraft('');
    };
    const removeAlias = (a) => setAliases((p) => p.filter((x) => x !== a));

    async function submit() {
        setSaving(true);
        try {
            const body = {
                stock_uom: uom,
                display_name: displayName.trim() || item.item_name,
                alt_names: aliases,
            };
            if (reorderLevel !== '') {
                const n = Number(reorderLevel);
                if (!Number.isFinite(n) || n < 0) {
                    toast.error('Reorder level must be a non-negative number.');
                    setSaving(false);
                    return;
                }
                body.reorder_level = n;
                body.reorder_uom = reorderUom || uom;
            } else {
                body.reorder_level = null;
                body.reorder_uom = null;
            }
            await api.setRawMaterialUom(item.item_id, body);
            toast.success(`Saved settings for ${body.display_name}.`);
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent data-testid="uom-edit-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Raw material settings</DialogTitle>
                    <DialogDescription>
                        Canonical unit, aliases, and optional reorder level for
                        &ldquo;{item.item_name}&rdquo;.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">Display name</Label>
                        <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                            className="mt-1" data-testid="uom-edit-name" />
                    </div>
                    <div>
                        <Label className="text-xs">Canonical UOM</Label>
                        <Select value={uom} onValueChange={setUom}>
                            <SelectTrigger className="mt-1" data-testid="uom-edit-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="max-h-64">
                                {uomList.map((u) => (
                                    <SelectItem key={u.key} value={u.key}>
                                        {u.key} <span className="text-muted-foreground text-xs ml-2">({u.class})</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Layer 1: aliases chip input */}
                    <div className="pt-3 border-t border-border/60">
                        <Label className="text-xs flex items-center gap-2">
                            Aliases / vendor variants
                            <span className="text-[10px] font-normal text-muted-foreground">
                                — future bills matching any of these resolve here
                            </span>
                        </Label>
                        <div className="flex flex-wrap gap-1.5 mt-1.5 mb-1.5">
                            {aliases.map((a) => (
                                <Badge key={a} variant="secondary" className="gap-1">
                                    <span className="font-mono text-[11px]">{a}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeAlias(a)}
                                        className="hover:text-destructive"
                                        aria-label={`Remove alias ${a}`}
                                        data-testid={`alias-remove-${a}`}
                                    >×</button>
                                </Badge>
                            ))}
                            {aliases.length === 0 && (
                                <span className="text-[11px] text-muted-foreground">No aliases yet.</span>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Input
                                value={aliasDraft}
                                onChange={(e) => setAliasDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(); } }}
                                placeholder='e.g. "VANILLA 5L TUB"'
                                className="h-8 font-mono"
                                data-testid="alias-input"
                            />
                            <Button size="sm" variant="outline" onClick={addAlias} disabled={!aliasDraft.trim()}>
                                Add
                            </Button>
                        </div>
                    </div>

                    <div className="pt-3 border-t border-border/60">
                        <Label className="text-xs flex items-center gap-2">
                            Reorder level (optional)
                            <span className="text-[10px] font-normal text-muted-foreground">
                                — nudges you when stock crosses this
                            </span>
                        </Label>
                        <div className="grid grid-cols-[1fr,90px] gap-2 mt-1">
                            <Input type="number" inputMode="decimal" min="0" step="0.001" value={reorderLevel}
                                onChange={(e) => setReorderLevel(e.target.value)}
                                placeholder="e.g. 500" className="font-mono tabular-nums"
                                data-testid="uom-edit-reorder-level" />
                            <Select value={reorderUom || uom} onValueChange={setReorderUom}>
                                <SelectTrigger data-testid="uom-edit-reorder-uom"><SelectValue /></SelectTrigger>
                                <SelectContent className="max-h-64">
                                    {uomList.map((u) => <SelectItem key={u.key} value={u.key}>{u.key}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} data-testid="uom-edit-save">
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/**
 * StockTakeSheet — physical stock reconciliation (Phase 7b-2).
 *
 * Owner workflow:
 *   1. Open sheet → we pull theoretical stock + best-effort unit costs
 *      via /stock/take-preview.
 *   2. Owner walks the shop with the app, punches in actual physical
 *      counts per raw material.
 *   3. Variance auto-computes live. Total wastage value ticks at the
 *      bottom so the owner sees P&L impact immediately.
 *   4. On save → variances hit the stock ledger (wastage OUT or
 *      correction IN) + optional Expense record for the wastage total.
 */
function StockTakeSheet({ open, onOpenChange, onSaved }) {
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [rows, setRows] = useState(null);           // preview rows
    const [notes, setNotes] = useState('');
    const [postExpense, setPostExpense] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setRows(null);
            try {
                const r = await api.stockTakePreview();
                setRows(
                    (r.items || []).map((row) => ({
                        ...row,
                        physical_qty: '',   // owner fills this in
                    })),
                );
            } catch (e) {
                setRows([]);
                toast.error('Could not load current stock.');
            }
        })();
    }, [open]);

    const updatePhysical = (idx, val) => {
        setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, physical_qty: val } : r)));
    };

    // Auto-compute variance + running wastage total.
    const totals = useMemo(() => {
        if (!rows) return { wastage: 0, correction: 0, filled: 0 };
        let wastage = 0;
        let correction = 0;
        let filled = 0;
        for (const r of rows) {
            if (r.physical_qty === '' || r.physical_qty == null) continue;
            filled++;
            const phys = Number(r.physical_qty);
            if (!Number.isFinite(phys)) continue;
            const variance = phys - Number(r.theoretical_qty || 0);
            const uc = Number(r.unit_cost || 0);
            if (!uc) continue;
            if (variance < 0) wastage += Math.abs(variance) * uc;
            else if (variance > 0) correction += variance * uc;
        }
        return {
            wastage: Math.round(wastage * 100) / 100,
            correction: Math.round(correction * 100) / 100,
            filled,
        };
    }, [rows]);

    async function submit() {
        if (!rows) return;
        // Only include rows the owner actually filled — skipping treats
        // as "not counted, don't touch."
        const payload = rows
            .filter((r) => r.physical_qty !== '' && r.physical_qty != null)
            .map((r) => ({
                item_id: r.item_id,
                item_name: r.item_name,
                uom: r.uom,
                theoretical_qty: Number(r.theoretical_qty) || 0,
                physical_qty: Number(r.physical_qty),
                unit_cost: r.unit_cost || null,
            }));
        if (payload.length === 0) {
            toast.error('Fill physical counts for at least one item.');
            return;
        }
        setSaving(true);
        try {
            const r = await api.createStockTake({
                taken_at: date,
                rows: payload,
                notes: notes.trim() || null,
                post_wastage_expense: postExpense,
            });
            const stats = r.stats || {};
            toast.success(
                `Stock take saved. Wastage ${fmtMoney(stats.wastage_value)} logged` +
                (stats.expense_id ? ' + expense recorded.' : '.'),
                { duration: 6000 },
            );
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save stock take.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-2xl overflow-y-auto flex flex-col"
                data-testid="stock-take-sheet"
            >
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <ClipboardCheck size={16} /> Physical stock take
                    </SheetTitle>
                    <SheetDescription>
                        Walk the shop, fill actual counts, save. Differences post to the
                        stock ledger as wastage or corrections.
                    </SheetDescription>
                </SheetHeader>

                <div className="mt-4 flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Taken on:</Label>
                    <Input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-[160px]"
                        data-testid="stock-take-date"
                    />
                </div>

                <div className="mt-3 flex-1 overflow-y-auto rounded-md border border-border/60">
                    {rows === null ? (
                        <div className="p-4 space-y-2">
                            {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-8" />)}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">
                            No raw materials to count yet. Scan a bill first.
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Raw material</th>
                                    <th className="text-right px-3 py-2 font-medium">Book qty</th>
                                    <th className="text-right px-3 py-2 font-medium">Physical</th>
                                    <th className="text-right px-3 py-2 font-medium">Δ</th>
                                    <th className="text-right px-3 py-2 font-medium">Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, idx) => {
                                    const phys = Number(r.physical_qty);
                                    const filled = r.physical_qty !== '' && Number.isFinite(phys);
                                    const variance = filled ? phys - Number(r.theoretical_qty || 0) : null;
                                    const uc = Number(r.unit_cost || 0);
                                    const value = filled && uc ? variance * uc : null;
                                    const varColor = variance == null
                                        ? 'text-muted-foreground'
                                        : variance < 0
                                            ? 'text-red-700 dark:text-red-400'
                                            : variance > 0
                                                ? 'text-green-700 dark:text-green-400'
                                                : 'text-muted-foreground';
                                    return (
                                        <tr key={r.item_id} className="border-t border-border/50">
                                            <td className="px-3 py-1.5">
                                                {r.item_name}
                                                <span className="ml-1.5 text-[10px] text-muted-foreground uppercase">{r.uom}</span>
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums font-mono text-muted-foreground">
                                                {Number(r.theoretical_qty || 0).toFixed(3)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right">
                                                <Input
                                                    type="number"
                                                    inputMode="decimal"
                                                    min="0"
                                                    step="0.001"
                                                    value={r.physical_qty}
                                                    onChange={(e) => updatePhysical(idx, e.target.value)}
                                                    className="h-7 text-right font-mono w-24 ml-auto"
                                                    data-testid={`stock-take-physical-${idx}`}
                                                    placeholder="—"
                                                />
                                            </td>
                                            <td className={`px-3 py-1.5 text-right tabular-nums font-mono ${varColor}`}>
                                                {variance == null ? '—' : (variance > 0 ? '+' : '') + variance.toFixed(3)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums font-mono text-muted-foreground">
                                                {value == null ? '—' : (value < 0 ? '-' : '+') + fmtMoney(Math.abs(value))}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="mt-3 space-y-2">
                    <Label className="text-xs">Notes (optional)</Label>
                    <Input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="e.g. after Friday close · full-shop count"
                        data-testid="stock-take-notes"
                    />
                    <label className="flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer pt-1">
                        <input
                            type="checkbox"
                            checked={postExpense}
                            onChange={(e) => setPostExpense(e.target.checked)}
                            className="mt-0.5"
                            data-testid="stock-take-post-expense"
                        />
                        Also post the wastage value as a &ldquo;Wastage / Spoilage&rdquo;
                        expense (feeds into P&amp;L)
                    </label>
                </div>

                <div className="mt-3 rounded-md border border-border/70 bg-muted/30 p-3 flex items-center justify-between gap-4">
                    <div className="text-[11px] text-muted-foreground">
                        {totals.filled} of {rows?.length || 0} items counted
                    </div>
                    <div className="flex items-center gap-4 text-xs">
                        {totals.wastage > 0 && (
                            <span className="text-red-700 dark:text-red-400 font-medium">
                                Wastage: {fmtMoney(totals.wastage)}
                            </span>
                        )}
                        {totals.correction > 0 && (
                            <span className="text-green-700 dark:text-green-400 font-medium">
                                Correction: +{fmtMoney(totals.correction)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-3 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving || totals.filled === 0}
                        data-testid="stock-take-save"
                    >
                        {saving ? 'Saving…' : `Save stock take (${totals.filled})`}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
}


/* ─────────────────────────────────────────────────────────────
 * Auto86Dialog — Phase 7c Feature #2.
 * When a raw material's stock hits ≤0, this dialog surfaces every
 * menu item whose recipe depends on it and lets the owner bulk-
 * deactivate them so counter staff can't take unfulfillable orders.
 * ───────────────────────────────────────────────────────────── */
function Auto86Dialog({ open, onOpenChange, item, onSaved }) {
    const [affected, setAffected] = useState(null);
    const [checked, setChecked] = useState(new Set());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setAffected(null);
            try {
                const r = await api.menuItemsBlockedBy(item.item_id);
                const items = r.items || [];
                setAffected(items);
                // Pre-check every currently-active affected item.
                setChecked(new Set(items.filter((x) => x.active !== false).map((x) => x.id)));
            } catch (e) {
                setAffected([]);
                toast.error('Could not load affected items.');
            }
        })();
    }, [open, item.item_id]);

    const toggle = (id) => setChecked((prev) => {
        const nxt = new Set(prev);
        if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
        return nxt;
    });

    async function submit() {
        if (checked.size === 0) { onOpenChange(false); return; }
        setSaving(true);
        try {
            // Batch — one PATCH per menu item. Small N, sequential is fine.
            let n = 0;
            for (const id of checked) {
                try {
                    await api.updateMenuItem(id, { active: false });
                    n++;
                } catch { /* skip individual failures */ }
            }
            toast.success(`Marked ${n} menu item${n === 1 ? '' : 's'} inactive.`);
            onSaved();
        } catch (e) {
            toast.error('Failed to update menu items.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-md" data-testid="auto-86-dialog">
                <DialogHeader>
                    <DialogTitle>86 menu items using {item.item_name}</DialogTitle>
                    <DialogDescription>
                        Stock is at <span className="font-mono">{item.qty} {item.stock_uom}</span>.
                        These menu items depend on it. Uncheck any you still want to keep sellable.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {affected === null ? (
                        <div className="space-y-2">
                            {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-9" />)}
                        </div>
                    ) : affected.length === 0 ? (
                        <div className="text-sm text-muted-foreground py-6 text-center">
                            No active menu items reference this ingredient.
                        </div>
                    ) : affected.map((mi) => (
                        <label
                            key={mi.id}
                            className="flex items-center gap-3 px-3 py-2 rounded border border-border/70 hover:bg-muted/40 cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                checked={checked.has(mi.id)}
                                onChange={() => toggle(mi.id)}
                                data-testid={`auto-86-check-${mi.id}`}
                            />
                            <div className="flex-1 text-sm">
                                {mi.name}
                                {mi.active === false && (
                                    <Badge variant="outline" className="ml-2 text-[10px] text-muted-foreground">already 86'd</Badge>
                                )}
                            </div>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={saving || checked.size === 0}
                        data-testid="auto-86-confirm"
                    >
                        {saving ? 'Applying…' : `86 ${checked.size} item${checked.size === 1 ? '' : 's'}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─────────────────────────────────────────────────────────────
 * VarianceReportSheet — Phase 7c Feature #3.
 * Weekly spillage/theft report. For a picked date range:
 *   Theoretical usage (from recipe-driven OUT ledger)
 *   Physical usage  (opening + IN in-range − closing)
 *   Variance         = Theoretical − Physical  (positive = missing)
 * ───────────────────────────────────────────────────────────── */
function VarianceReportSheet({ open, onOpenChange }) {
    // Default to the last 7 days ending yesterday — the standard weekly window.
    const defaultRange = useMemo(() => {
        const end = new Date();
        end.setDate(end.getDate() - 1);
        const start = new Date(end);
        start.setDate(start.getDate() - 6);
        const iso = (d) => d.toISOString().slice(0, 10);
        return { start: iso(start), end: iso(end) };
    }, []);
    const [start, setStart] = useState(defaultRange.start);
    const [end, setEnd] = useState(defaultRange.end);
    const [rows, setRows] = useState(null);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(false);

    async function run() {
        setLoading(true);
        try {
            const r = await api.stockVarianceReport({ start, end });
            setRows(r.items || []);
            setTotals(r.totals || {});
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to compute report.');
        } finally { setLoading(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (open) run(); }, [open]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <BarChart3 size={16} /> Variance report
                    </SheetTitle>
                    <SheetDescription>
                        Theoretical (from recipes) vs Physical (from ledger) usage.
                        Positive variance = unaccounted loss.
                    </SheetDescription>
                </SheetHeader>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                    <Label className="text-xs">From</Label>
                    <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-[140px]" data-testid="variance-start" />
                    <Label className="text-xs">To</Label>
                    <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-[140px]" data-testid="variance-end" />
                    <Button size="sm" onClick={run} disabled={loading} data-testid="variance-run">
                        {loading ? 'Loading…' : 'Recompute'}
                    </Button>
                </div>

                {totals.variance_value != null && (
                    <div className="mt-3 rounded-md border border-border/70 bg-muted/40 p-3">
                        <div className="text-xs text-muted-foreground">
                            Total unaccounted loss for the period
                        </div>
                        <div className={`text-2xl font-heading font-semibold ${totals.variance_value > 0 ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}
                            data-testid="variance-total">
                            {fmtMoney(Math.abs(totals.variance_value))}
                            {totals.variance_value > 0 ? ' lost' : ' surplus'}
                        </div>
                    </div>
                )}

                <div className="mt-3">
                    {rows === null ? (
                        <div className="space-y-2">
                            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-9" />)}
                        </div>
                    ) : rows.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-8">
                            No ledger activity in this range.
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-md border border-border/60">
                            <table className="w-full text-sm">
                                <thead className="bg-muted/40 text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-3 py-2 font-medium">Raw material</th>
                                        <th className="text-right px-3 py-2 font-medium">Theoretical</th>
                                        <th className="text-right px-3 py-2 font-medium">Physical</th>
                                        <th className="text-right px-3 py-2 font-medium">Variance</th>
                                        <th className="text-right px-3 py-2 font-medium">₹ loss</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r) => {
                                        const bad = (r.variance_value || 0) > 0;
                                        return (
                                            <tr key={r.item_id} className="border-t border-border/60">
                                                <td className="px-3 py-1.5">
                                                    {r.item_name}
                                                    <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">{r.stock_uom}</span>
                                                </td>
                                                <td className="px-3 py-1.5 text-right tabular-nums font-mono text-muted-foreground">
                                                    {r.theoretical_usage}
                                                </td>
                                                <td className="px-3 py-1.5 text-right tabular-nums font-mono text-muted-foreground">
                                                    {r.physical_usage}
                                                </td>
                                                <td className={`px-3 py-1.5 text-right tabular-nums font-mono ${bad ? 'text-red-700 dark:text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                                                    {r.variance > 0 ? '+' : ''}{r.variance}
                                                </td>
                                                <td className={`px-3 py-1.5 text-right tabular-nums font-mono ${bad ? 'text-red-700 dark:text-red-400 font-semibold' : 'text-muted-foreground'}`}>
                                                    {r.variance_value != null
                                                        ? (bad ? '-' : '+') + fmtMoney(Math.abs(r.variance_value))
                                                        : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
}


/* ─────────────────────────────────────────────────────────────
 * MergeRawMaterialsDialog — Sprint 2026-08 · Layer 2.
 * Owner selects N rows in Stock tab → clicks "Merge N" → picks
 * canonical → all ledger/recipe references get rewritten and the
 * merged item_masters get folded into canonical.alt_names.
 * ───────────────────────────────────────────────────────────── */
function MergeRawMaterialsDialog({ open, onOpenChange, selectedIds, levels, onSaved }) {
    const [canonical, setCanonical] = useState(selectedIds[0] || '');
    const [saving, setSaving] = useState(false);

    const rows = useMemo(
        () => selectedIds.map((id) => levels.find((l) => l.item_id === id)).filter(Boolean),
        [selectedIds, levels],
    );

    async function submit() {
        if (!canonical) return;
        const merge_ids = selectedIds.filter((i) => i !== canonical);
        if (merge_ids.length === 0) {
            toast.error('Pick at least one non-canonical item.');
            return;
        }
        setSaving(true);
        try {
            const r = await api.mergeRawMaterials({ canonical_id: canonical, merge_ids });
            const s = r.stats || {};
            toast.success(
                `Merged ${merge_ids.length} into "${canonical}". ` +
                `Rewrote ${s.ledger_entries_moved} ledger entries + ${s.recipes_updated} recipes.`,
                { duration: 6000 },
            );
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Merge failed.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-lg" data-testid="merge-dialog">
                <DialogHeader>
                    <DialogTitle>Merge {selectedIds.length} raw materials</DialogTitle>
                    <DialogDescription>
                        Pick the one to keep as canonical. The others get folded in as
                        aliases — every ledger entry + recipe reference will be rewritten.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {rows.map((r) => (
                        <label
                            key={r.item_id}
                            className="flex items-start gap-3 px-3 py-2 rounded border border-border/70 hover:bg-muted/40 cursor-pointer"
                        >
                            <input
                                type="radio"
                                name="canonical"
                                checked={canonical === r.item_id}
                                onChange={() => setCanonical(r.item_id)}
                                className="mt-1"
                                data-testid={`merge-canonical-${r.item_id}`}
                            />
                            <div className="flex-1 text-sm">
                                <div className="font-medium">{r.item_name}</div>
                                <div className="text-[11px] text-muted-foreground font-mono">
                                    {r.item_id} · {r.qty} {r.stock_uom}
                                </div>
                            </div>
                        </label>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button onClick={submit} disabled={saving || !canonical} data-testid="merge-confirm">
                        {saving ? 'Merging…' : 'Merge'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─────────────────────────────────────────────────────────────
 * ClusterSuggestionsDialog — Sprint 2026-08 · Layer 3.
 * Emergent LLM (Gemini Flash) analyzes the full raw material list
 * and suggests high-confidence merges (same product, same pack
 * size). Owner ticks the ones to apply and hits "Merge selected".
 * ───────────────────────────────────────────────────────────── */
function ClusterSuggestionsDialog({ open, onOpenChange, onSaved }) {
    const [clusters, setClusters] = useState(null);
    const [reason, setReason] = useState('');
    const [checked, setChecked] = useState(new Set());
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setClusters(null);
            setReason('');
            try {
                const r = await api.clusterSuggestions();
                setClusters(r.clusters || []);
                setReason(r.reason || '');
                // Pre-check every suggested cluster.
                setChecked(new Set((r.clusters || []).map((c) => c.canonical_id)));
            } catch (e) {
                setClusters([]);
                toast.error('Cluster analysis failed.');
            }
        })();
    }, [open]);

    const toggle = (id) => setChecked((prev) => {
        const nxt = new Set(prev);
        if (nxt.has(id)) nxt.delete(id); else nxt.add(id);
        return nxt;
    });

    async function submit() {
        if (checked.size === 0) { onOpenChange(false); return; }
        setSaving(true);
        try {
            let mergedCount = 0;
            for (const c of clusters) {
                if (!checked.has(c.canonical_id)) continue;
                try {
                    await api.mergeRawMaterials({
                        canonical_id: c.canonical_id,
                        merge_ids: c.member_ids,
                    });
                    mergedCount++;
                } catch { /* skip individual failures */ }
            }
            toast.success(`Applied ${mergedCount} merge(s).`, { duration: 5000 });
            onSaved();
        } catch (e) {
            toast.error('Merge run failed.');
        } finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cluster-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles size={16} className="text-primary" /> Suggested duplicates
                    </DialogTitle>
                    <DialogDescription>
                        High-confidence clusters where multiple raw materials look like the same
                        product (same pack size). Uncheck any you want to skip.
                    </DialogDescription>
                </DialogHeader>
                {clusters === null ? (
                    <div className="space-y-2 py-4">
                        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
                        <p className="text-xs text-muted-foreground text-center pt-2">
                            AI is analyzing your raw material list…
                        </p>
                    </div>
                ) : clusters.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                        No high-confidence duplicates found.
                        {reason && <div className="text-[11px] mt-1">{reason}</div>}
                    </div>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {clusters.map((c) => (
                            <label
                                key={c.canonical_id}
                                className="flex items-start gap-3 px-3 py-2.5 rounded border border-border/70 hover:bg-muted/40 cursor-pointer"
                            >
                                <input
                                    type="checkbox"
                                    checked={checked.has(c.canonical_id)}
                                    onChange={() => toggle(c.canonical_id)}
                                    className="mt-1"
                                    data-testid={`cluster-check-${c.canonical_id}`}
                                />
                                <div className="flex-1 text-sm">
                                    <div className="font-medium">
                                        Keep &ldquo;{c.canonical_name}&rdquo;
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        Merge in: {c.member_names.join(' · ')}
                                    </div>
                                    {c.reason && (
                                        <div className="text-[10px] italic text-muted-foreground mt-1">
                                            {c.reason}
                                        </div>
                                    )}
                                </div>
                            </label>
                        ))}
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={saving || checked.size === 0 || (clusters?.length === 0)}
                        data-testid="cluster-apply"
                    >
                        {saving ? 'Merging…' : `Apply ${checked.size} merge${checked.size === 1 ? '' : 's'}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
